import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress, Typography } from '@material-ui/core';
import moment from 'moment';

import * as actions from '../../../store/actions/index';
import useDatosComprobante from '../hooks/useDatosComprobante';
import { formatNumberWithMonedaSigno } from '../../../assets/shared/formatNumber';
import { texto } from '../textosComprobante';
import colores from '../Bandeja/coloresBandeja';

const useStyles = makeStyles(() => ({
  valores: {
    marginTop: '6px',
    paddingTop: '6px',
    borderTop: `1px dashed ${colores.borde}`,
    fontSize: '12px'
  },
  valor: {
    padding: '3px 0'
  },
  linea: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px'
  },
  detalle: {
    color: colores.tenue,
    paddingLeft: '10px'
  },
  total: {
    fontWeight: 500,
    paddingTop: '4px'
  },
  aviso: {
    marginTop: '6px',
    padding: '6px 8px',
    borderRadius: '4px',
    backgroundColor: '#fff8d6',
    color: colores.secundario
  }
}));

const fecha = (valor) => (valor ? moment(valor).format('DD/MM/YYYY') : null);
const conValor = (valor) => valor && texto(valor) !== '0';

/** Datos de cada tipo de valor (Tipos_Valor) que se muestran debajo de su nombre. */
const detallesPorTipo = {
  CH: (valor) => [valor.banco, conValor(valor.numero) && `N° ${texto(valor.numero)}`, fecha(valor.fechaVencimiento) && `vence ${fecha(valor.fechaVencimiento)}`, valor.firmante],
  DB: (valor) => [[valor.cuenta, valor.nroCuenta].filter(Boolean).join(' '), conValor(valor.numero) && `N° ${texto(valor.numero)}`],
  RT: (valor) => {
    const certificado = conValor(valor.certificado) ? valor.certificado : valor.numero;
    return [conValor(certificado) && `Cert. ${texto(certificado)}`, valor.jurisdiccion && `Jurisd. ${valor.jurisdiccion}`, fecha(valor.fecha)];
  },
  CU: (valor) => [valor.tarjeta, conValor(valor.numero) && `Cupón ${texto(valor.numero)}`, valor.cuotas > 0 && `${valor.cuotas} cuotas`]
};

const detalleValor = (valor) => {
  const armar = detallesPorTipo[valor.idTipo];
  return armar ? armar(valor).filter(Boolean).join(' · ') : '';
};

/**
 * Valores con que se pagó un recibo (se piden al montarse, es decir al desplegarlo) y, si también se imputó a
 * comprobantes que no están en el circuito, el aviso con cuáles. El Eiffel no guarda qué valor pagó cada factura:
 * los valores son del recibo completo.
 */
const ValoresRecibo = ({ recibo, circuito }) => {
  const classes = useStyles();
  const { datos, error } = useDatosComprobante(recibo, actions.loadValoresReciboImpresion);

  if (error) return <Typography color="error" variant="caption">{error}</Typography>;
  if (!datos) {
    return (
      <div className={classes.valores}>
        <CircularProgress size={14} />
      </div>
    );
  }

  const enCircuito = new Set(circuito ? circuito.todos.map((nodo) => nodo.clave) : []);
  const otras = datos.imputaciones.filter((imputacion) => !enCircuito.has(imputacion.claveCruce));

  return (
    <div className={classes.valores}>
      {datos.valores.length === 0 && <div className={classes.detalle}>El recibo no tiene valores cargados.</div>}
      {datos.valores.map((valor, indice) => {
        const detalle = detalleValor(valor);
        return (
          <div key={`${valor.idValor}-${indice}`} className={classes.valor}>
            <div className={classes.linea}>
              <span>{valor.nombre || valor.nombreTipo || valor.idValor}</span>
              <span>{formatNumberWithMonedaSigno(valor.monto)}</span>
            </div>
            {detalle && <div className={classes.detalle}>{detalle}</div>}
          </div>
        );
      })}
      <div className={`${classes.linea} ${classes.total}`}>
        <span>Total del recibo</span>
        <span>{formatNumberWithMonedaSigno(recibo.total)}</span>
      </div>
      {otras.length > 0 && (
        <div className={classes.aviso}>
          Este recibo también paga:{' '}
          {otras
            .map((imputacion) => `${texto(imputacion.nombreComprobante)} ${texto(imputacion.nroExterno) || imputacion.nroReferenciaCruce} (${formatNumberWithMonedaSigno(imputacion.importe)})`)
            .join(', ')}
        </div>
      )}
    </div>
  );
};

export default ValoresRecibo;
